import React, { FC, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { Box } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useI18n } from '~Hooks';
import { useInventoryUniverse } from '~Components/InventoryUniverse';

const IndicatorBall = styled(Box)`
  border-radius: 50%;
  width: 12px;
  height: 12px;
`;

const AbsoluteCenter = styled(Box)<{ left: string }>`
  position: absolute;
  left: ${({ left }) => left};
  transform: translateX(-50%);
`;

const StockColumn = styled(AbsoluteCenter)`
  width: 120px;
  min-width: 120px;
`;

const ItalicParagraph = styled(Paragraph)`
  font-style: italic;
`;

interface Props {
  itemId: string;
}

const StockStatus: FC<Props> = ({ itemId }) => {
  const { i18n } = useI18n();
  const inventory = useInventoryUniverse();

  const inventoryStatus = useMemo(() => {
    const inventoryItem = inventory.items.find((i) => i.itemId === itemId);

    if (inventoryItem) {
      return {
        count: inventoryItem.count,
        outOfStock: inventoryItem.outOfStock,
      };
    }

    return {
      count: null,
      outOfStock: false,
    };
  }, [inventory, itemId]);

  const hasInventory = inventoryStatus.outOfStock || inventoryStatus.count !== null;

  return (
    <StockColumn left="55%" display="inline-flex" alignItems="center">
      <IndicatorBall
        backgroundColor={
          !hasInventory
            ? 'colorStatusGray'
            : inventoryStatus.outOfStock
            ? 'colorStatusRed'
            : 'colorStatusGreen'
        }
        marginRight="xs"
      />
      {hasInventory ? (
        <Paragraph level={5} color="var(--colorBlueGray200)">
          {inventoryStatus.outOfStock
            ? i18n('Inventory.OutOfStock')
            : `${inventoryStatus.count} ${i18n('Inventory.XInStock')}`}
        </Paragraph>
      ) : (
        <ItalicParagraph level={5} color="var(--colorBlueGray200)">
          {i18n('Inventory.StockNotSet')}
        </ItalicParagraph>
      )}
    </StockColumn>
  );
};

export default memo(StockStatus);
